import { useCallback, useState } from "react";
import { useConvertMediaMutation } from "graphql/mutations/convert-media.generated";
import {
	useUploadingFiles,
	UploadingFile,
	UploadScope,
	UploadingFilesScope
} from "../state/reactive-vars/uploading-files-var";
import { message } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { isHeic } from "heic-to";
import { needsConversion, uploadToStorage, convertHeicFileToJpeg, isSupportedFileType } from "utils/files-management";
import { AiChatType } from "constants/field-values";

export type { UploadingFile };

// Helper function to convert file path to gs:// URI
function createGsUri(filePath: string, bucketName: string): string {
	return `gs://${bucketName}/${filePath}`;
}

// Where the duplicate warning points the user, per upload slot.
const DUPLICATE_TARGET_LABELS: Partial<Record<UploadingFilesScope, string>> = {
	[UploadScope.PLAN_REVIEW]: "this package",
	[UploadScope.PLAN_REVIEW_GUIDELINES]: "this extraction",
	[UploadScope.JURISDICTION_RESOURCES]: "your resources"
};

export function useFileUpload(scope: UploadingFilesScope = AiChatType.CLIENT) {
	const { uploadingFiles, updateUploadingFiles: setUploadingFiles } = useUploadingFiles(scope);
	const [isConverting, setIsConverting] = useState(false);
	const [convertMediaMutation] = useConvertMediaMutation();

	/**
	 * Returns converted file or original file if conversion not needed.
	 * Null if conversion fails
	 */
	const convertHeicIfNeeded = useCallback(async (file: RcFile): Promise<RcFile | null> => {
		setIsConverting(true);

		let isHeicResult = await isHeic(file);

		// Fallback: Check file extension if MIME type is empty or isHeic fails
		if (!isHeicResult) {
			const fileName = file.name.toLowerCase();
			const hasHeicExtension = fileName.endsWith(".heic") || fileName.endsWith(".heif");

			if (hasHeicExtension) {
				console.log("Detected HEIC by extension, attempting conversion");
				isHeicResult = true;
			}
		}

		if (!isHeicResult) {
			console.log("File is not HEIC, returning original file");
			setIsConverting(false);
			return file;
		}

		try {
			const base = await convertHeicFileToJpeg(file);
			const converted = Object.assign(base, { uid: file.uid }) as RcFile;

			setIsConverting(false);
			return converted;
		} catch (error) {
			console.error("HEIC conversion failed:", error);

			// Check for specific error messages
			const errorMessage = error instanceof Error ? error.message : String(error);
			if (errorMessage.includes("File size too small") || errorMessage.includes("HEIF image not found")) {
				message.error("The HEIC file appears to be corrupted or empty. Please try a different image.");
			} else {
				message.error("Failed to convert HEIC image. Please try a different format.");
			}

			setIsConverting(false);
			return null;
		}
	}, []);

	const uploadFiles = useCallback(
		async (files: FileList | File[]) => {
			const fileArray = Array.from(files);

			// Validate file types and sizes
			const maxSize = 50 * 1024 * 1024; // 50MB

			const validFiles = fileArray.filter(file => {
				// Warn about large files but don't block them
				if (file.size > maxSize) {
					console.warn(
						`⚠️ File ${file.name} is over 50MB (${(file.size / 1024 / 1024).toFixed(2)}MB). This function hasn't been optimized for large files and may not work correctly.`
					);
				}
				if (!isSupportedFileType(file.type)) {
					message.error(`File type ${file.type} is not supported.`);
					return false;
				}
				return true;
			});

			if (validFiles.length === 0) return [];

			// Check for duplicates - compare by name, size, and lastModified timestamp
			const duplicates: string[] = [];
			const uniqueFiles = validFiles.filter(file => {
				const isDuplicate = uploadingFiles.some(
					existingFile =>
						existingFile.file.name === file.name &&
						existingFile.file.size === file.size &&
						existingFile.file.lastModified === file.lastModified
				);

				if (isDuplicate) {
					duplicates.push(file.name);
					return false;
				}
				return true;
			});

			// Notify user about duplicates
			if (duplicates.length > 0) {
				const filesList =
					duplicates.length === 1
						? duplicates[0]
						: duplicates.slice(0, -1).join(", ") + " and " + duplicates[duplicates.length - 1];
				const target = DUPLICATE_TARGET_LABELS[scope] ?? "this message";
				message.warning(`${filesList} ${duplicates.length === 1 ? "is" : "are"} already attached to ${target}.`);
			}

			if (uniqueFiles.length === 0) return [];

			// Process HEIC files before upload
			const processedFiles = await Promise.all(
				uniqueFiles.map(async file => {
					const convertedFile = await convertHeicIfNeeded(file as RcFile);
					if (convertedFile === null) {
						return null; // HEIC conversion failed
					}
					return convertedFile as File;
				})
			);

			// Filter out files that failed conversion
			const validProcessedFiles = processedFiles.filter((file): file is File => file !== null);

			if (validProcessedFiles.length === 0) return [];

			// Create initial upload states
			const newUploadingFiles: UploadingFile[] = validProcessedFiles.map(file => ({
				id: `${Date.now()}-${Math.random()}`,
				file,
				progress: 0,
				status: "uploading" as const,
				fileName: file.name,
				mimeType: file.type
			}));

			setUploadingFiles(prev => [...prev, ...newUploadingFiles]);

			// Upload each file
			const uploadPromises = newUploadingFiles.map(async uploadingFile => {
				try {
					const uploadResult = await uploadToStorage(uploadingFile.file, progress => {
						setUploadingFiles(prev => prev.map(f => (f.id === uploadingFile.id ? { ...f, progress } : f)));
					});

					// Create gs:// URI
					const gsUri = createGsUri(uploadResult.filePath, uploadResult.bucketName);

					// If this format needs backend conversion, kick it off now
					if (needsConversion(uploadingFile.file.type)) {
						setUploadingFiles(prev =>
							prev.map(f =>
								f.id === uploadingFile.id
									? {
											...f,
											status: "converting" as const,
											progress: 100,
											fileKey: uploadResult.filePath,
											gsUri,
											publicUrl: uploadResult.publicUrl,
											mimeType: uploadingFile.file.type,
											fileName: uploadingFile.file.name
										}
									: f
							)
						);

						try {
							const { data } = await convertMediaMutation({
								variables: {
									gs_uri: gsUri,
									file_key: uploadResult.filePath,
									mime_type: uploadingFile.file.type,
									file_name: uploadingFile.file.name
								}
							});

							const converted = data?.convert_media;
							if (!converted?.success) {
								throw new Error(converted?.error || "Conversion failed");
							}

							setUploadingFiles(prev =>
								prev.map(f =>
									f.id === uploadingFile.id
										? {
												...f,
												status: "completed" as const,
												fileKey: converted.file_key ?? undefined,
												gsUri: converted.gs_uri ?? undefined,
												publicUrl: converted.public_url ?? undefined,
												mimeType: converted.mime_type ?? undefined
											}
										: f
								)
							);

							return {
								id: uploadingFile.id,
								fileKey: converted.file_key,
								gsUri: converted.gs_uri,
								publicUrl: converted.public_url,
								mimeType: converted.mime_type,
								fileName: uploadingFile.file.name
							};
						} catch (convError) {
							console.error("Media conversion failed:", convError);
							// Fall back to original upload so the file isn't lost
							setUploadingFiles(prev =>
								prev.map(f =>
									f.id === uploadingFile.id
										? {
												...f,
												status: "completed" as const,
												fileKey: uploadResult.filePath,
												gsUri,
												publicUrl: uploadResult.publicUrl,
												mimeType: uploadingFile.file.type,
												fileName: uploadingFile.file.name
											}
										: f
								)
							);

							return {
								id: uploadingFile.id,
								fileKey: uploadResult.filePath,
								gsUri,
								publicUrl: uploadResult.publicUrl,
								mimeType: uploadingFile.file.type,
								fileName: uploadingFile.file.name
							};
						}
					}

					// Mark as completed (no conversion needed)
					setUploadingFiles(prev =>
						prev.map(f =>
							f.id === uploadingFile.id
								? {
										...f,
										status: "completed" as const,
										progress: 100,
										fileKey: uploadResult.filePath,
										gsUri: gsUri,
										publicUrl: uploadResult.publicUrl,
										mimeType: uploadingFile.file.type,
										fileName: uploadingFile.file.name
									}
								: f
						)
					);

					return {
						id: uploadingFile.id,
						fileKey: uploadResult.filePath,
						gsUri: gsUri,
						publicUrl: uploadResult.publicUrl,
						mimeType: uploadingFile.file.type,
						fileName: uploadingFile.file.name
					};
				} catch (error) {
					console.error("Upload failed:", error);

					setUploadingFiles(prev =>
						prev.map(f =>
							f.id === uploadingFile.id
								? {
										...f,
										status: "error" as const,
										error: error instanceof Error ? error.message : "Upload failed"
									}
								: f
						)
					);

					message.error(`Failed to upload ${uploadingFile.file.name}`);
					return null;
				}
			});

			const results = await Promise.all(uploadPromises);
			return results.filter(Boolean);
		},
		[uploadingFiles, convertHeicIfNeeded, scope]
	);

	const removeFile = useCallback(
		(fileId: string) => {
			setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
		},
		[setUploadingFiles]
	);

	const clearAllFiles = useCallback(() => {
		setUploadingFiles([]);
	}, [setUploadingFiles]);

	const getCompletedFiles = useCallback(() => {
		return uploadingFiles
			.filter(f => f.status === "completed" && f.fileKey)
			.map(f => ({
				id: f.id,
				fileKey: f.fileKey!,
				gsUri: f.gsUri!,
				publicUrl: f.publicUrl,
				mimeType: f.mimeType!,
				fileName: f.fileName!
			}));
	}, [uploadingFiles]);

	return {
		uploadingFiles,
		uploadFiles,
		removeFile,
		clearAllFiles,
		getCompletedFiles,
		isUploading: uploadingFiles.some(f => f.status === "uploading" || f.status === "converting"),
		hasFiles: uploadingFiles.length > 0,
		convertHeicIfNeeded,
		isConverting
	};
}
